import { AxiosInstance } from 'axios';
import { getScimConfig } from '../utils/envUtils';
import { debugVerboseLog } from '../utils/debugLog';
import { getPluginFunctionPivotApiPath, getPluginFunctionPivotEntityApiPath, getPluginFunctionPivotDetailsApiPath, getPluginFunctionInvokeApiPath } from '../utils/pluginFunctionUtils';
import { ScimAuthService } from './ScimAuthService';
import { getAxiosInstance } from './axiosInstance';
import { School, Building, Class, ResponsibilityCenter } from '../types/Localities';
import { ScimGroup, GroupsResponse } from '../types/Groups';
import { Vacancy } from '../types/Vacancy';
import type { RequestApprovalTypePivotListItem } from '../types/EntityEntitlements';

export interface PaginatedResponse<T> {
    /** Row data (preferred: use `items` for list shape) */
    data: T[];
    /** Row data - same as data, for list-as-object shape so list-level $ and other props can live alongside items */
    items: T[];
    totalResults: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    itemsPerPage: number;
    hasMore?: boolean; // For journal entries - indicates if there are more pages (totalResults is always 0)
    [key: string]: any; // Allow list-level $ (e.g. $timeTaken, $caller, $debug) and other response props
}

export interface CallEntityPivotOptions {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    filter?: string;
    abortSignal?: AbortSignal;
    searchtext?: string;
    searchAttributes?: string;
    omitSearchDialectExpression?: boolean;
    /** When provided, forwarded to axios meta so x-on-behalf-of header is applied. */
    onBehalfOfUpn?: string;
    /** Optional override; when set, sent as query param. */
    skipReferenceExpansion?: boolean;
}

export class ScimEntityService {
    private api: AxiosInstance;
    private baseUrl: string;

    constructor() {
        const scimConfig = getScimConfig();
        this.baseUrl = scimConfig.apiUrl;
        this.api = getAxiosInstance(this.baseUrl);
    }

    /**
     * Add Debug parameter to params if debug mode is enabled
     */
    private async addDebugParam(params: any): Promise<void> {
        const { debugService } = await import('./DebugService');
        if (debugService.isEnabled()) {
            params.Debug = 'true';
        }
    }

    /**
     * Generic method to fetch entities from API
     */
    private async fetchEntities<T>(
        endpoint: string,
        page: number = 1,
        pageSize: number = 25,
        sortBy?: string,
        filter?: string,
        attributes?: string[],
        abortSignal?: AbortSignal,
        searchtext?: string,
        searchAttributes?: string,
        onBehalfOfUpn?: string,
        countOnly?: boolean,
        omitSearchDialectExpression?: boolean,
        skipReferenceExpansion: boolean = true
    ): Promise<PaginatedResponse<T>> {
        const startIndex = ((page - 1) * pageSize) + 1;
        const params: any = {
            startIndex,
            count: pageSize,
            skipReferenceExpansion
        };

        if (countOnly === true) {
            params.countOnly = true;
        }

        if (sortBy) {
            params.sortBy = sortBy;
        }

        if (attributes && attributes.length > 0) {
            params.attributes = attributes.join(',');
        }

        if (filter) {
            // Filter should be a plain string - axios will URL-encode it automatically
            // Don't double-encode if it's already encoded
            // Filter comes last to ensure proper parameter order in URL
            params.filter = filter;
        }

        if (searchtext) {
            // Backend expects searchText (camelCase) for simple search queries
            params.searchText = searchtext;

            // "Expression" checkbox controls dialect; send explicit dialect so behavior is unambiguous.
            params.searchdialect = omitSearchDialectExpression ? 'freetext' : 'expression';

            // Add searchAttributes if provided (comma-separated list of attributes to search)
            // If not provided, backend defaults to DisplayName
            if (searchAttributes) {
                params.searchAttributes = searchAttributes;
                debugVerboseLog('🔍 ScimEntityService: Adding searchAttributes parameter:', searchAttributes);
            } else {
                debugVerboseLog('🔍 ScimEntityService: No searchAttributes provided, backend will default to DisplayName');
            }
        }

        // Add Debug parameter if debug mode is enabled
        await this.addDebugParam(params);

        const apiResponse = await this.api.get(endpoint, {
            params: params,
            signal: abortSignal,
            ...(onBehalfOfUpn != null && onBehalfOfUpn !== '' ? { meta: { onBehalfOfUpn } } : {})
        });

        const responseData = apiResponse.data;
        const entities = countOnly === true ? [] : (responseData.Resources || []);
        const totalResults = responseData.totalResults || 0;
        const itemsPerPage = responseData.itemsPerPage || pageSize;
        const hasMore = responseData.$hasMore !== undefined ? responseData.$hasMore : undefined;
        const totalPages = Math.ceil(totalResults / itemsPerPage);
        const hasNextPage = hasMore !== undefined ? hasMore : (page < totalPages);
        const hasPreviousPage = page > 1;

        // Keep list-level $ properties on the response wrapper only (for $ tab); do not copy onto each item
        const listLevelDollarProps = responseData && typeof responseData === 'object'
            ? Object.fromEntries(Object.entries(responseData).filter(([key]) => key.startsWith('$')))
            : {};
        // List-level _ properties: on the response wrapper (for $ tab) and copied onto each item
        const listLevelUnderscoreProps = responseData && typeof responseData === 'object'
            ? Object.fromEntries(Object.entries(responseData).filter(([key]) => key.startsWith('_')))
            : {};

        // Tag entities with metadata about how they were fetched; copy list-level _ onto each item
        const taggedEntities = entities.map((entity: any) => {
            if (entity && typeof entity === 'object') {
                const taggedEntity = { ...entity, ...listLevelUnderscoreProps };
                // Tag skipReferenceExpansion (always true for fetchEntities)
                taggedEntity._skipReferenceExpansion = true;
                // Tag _attributesRequested only if attributes were actually provided and not empty (for backwards compatibility)
                if (attributes && attributes.length > 0) {
                    taggedEntity._attributesRequested = [...attributes];
                }
                return taggedEntity;
            }
            return entity;
        });

        // Surface client-added _ properties from items at list level so the $ tab can show them (they are added in the mapping above, not from the API)
        const firstTagged = taggedEntities[0];
        const clientUnderscoreProps = firstTagged && typeof firstTagged === 'object'
            ? Object.fromEntries(Object.entries(firstTagged).filter(([k]) => k.startsWith('_')))
            : {};

        return {
            ...listLevelDollarProps,
            ...listLevelUnderscoreProps,
            ...clientUnderscoreProps,
            data: taggedEntities,
            items: taggedEntities,
            totalResults,
            currentPage: page,
            totalPages,
            hasNextPage,
            hasPreviousPage,
            itemsPerPage,
            hasMore
        };
    }

    /**
     * Fetch Responsibility Centers from the SCIM API
     */
    async getResponsibilityCenters(page: number = 1, pageSize: number = 25, sortBy?: string): Promise<PaginatedResponse<ResponsibilityCenter>> {
        return this.fetchEntities<ResponsibilityCenter>('/entities/ResponsibilityCenter', page, pageSize, sortBy);
    }

    /**
     * Fetch Schools from the SCIM API
     */
    async getSchools(page: number = 1, pageSize: number = 999, sortBy?: string, filter?: string): Promise<PaginatedResponse<School>> {
        return this.fetchEntities<School>('/entities/School', page, pageSize, sortBy, filter);
    }

    /**
     * Fetch Buildings from the SCIM API
     */
    async getBuildings(page: number = 1, pageSize: number = 25, sortBy?: string): Promise<PaginatedResponse<Building>> {
        return this.fetchEntities<Building>('/entities/Building', page, pageSize, sortBy);
    }

    /**
     * Fetch Classes from the SCIM API
     */
    async getClasses(page: number = 1, pageSize: number = 25, sortBy?: string): Promise<PaginatedResponse<Class>> {
        return this.fetchEntities<Class>('/entities/Class', page, pageSize, sortBy);
    }

    /**
     * Fetch Groups from the SCIM API
     */
    async getGroups(page: number = 1, pageSize: number = 25, sortBy?: string, attributes?: string[], abortSignal?: AbortSignal): Promise<GroupsResponse> {
        const response = await this.fetchEntities<any>('/entities/Group', page, pageSize, sortBy, undefined, attributes, abortSignal);

        // Map to Pascal case properties to match the schema
        // Preserve ALL properties from the original response (including $Group, score properties, etc.)
        const groups = response.data.map((group: any) => {
            // Store original raw data BEFORE any modifications (preserves all API response properties)
            const originalRawData = { ...group };

            // Start with a copy of all properties from the original group (preserves metadata like _skipReferenceExpansion, _attributesRequested)
            const mappedGroup: any = { ...group };

            // Ensure base properties use Pascal case (but don't overwrite if already present)
            mappedGroup.Id = group.Id || group.id || mappedGroup.Id;
            mappedGroup.DisplayName = group.DisplayName || group.displayName || mappedGroup.DisplayName;
            mappedGroup.Description = group.Description || group.description || mappedGroup.Description;
            mappedGroup.Email = group.Email || group.email || mappedGroup.Email;
            mappedGroup.EntityType = group.EntityType || group.entityType || mappedGroup.EntityType;
            mappedGroup.AccountName = group.AccountName || group.accountName || mappedGroup.AccountName;
            mappedGroup.EntraId = group.EntraId || group.entraId || mappedGroup.EntraId;
            mappedGroup.Member = group.Member || group.member || mappedGroup.Member;
            mappedGroup.MemberOf = group.MemberOf || group.memberOf || mappedGroup.MemberOf;
            mappedGroup.MemberTransitive = group.MemberTransitive || group.memberTransitive || mappedGroup.MemberTransitive;
            mappedGroup.Scope = group.Scope || group.scope || mappedGroup.Scope;
            mappedGroup.Type = group.Type || group.type || mappedGroup.Type;

            // Explicitly store rawData for consistency with other entity types (like classes)
            // This ensures all properties from the API response are accessible, including $Group, score properties, etc.
            // Store the original raw data (before any property mapping) to preserve all API response properties
            mappedGroup.rawData = originalRawData;

            return mappedGroup;
        });

        const listLevelMeta = response && typeof response === 'object'
            ? Object.fromEntries(Object.entries(response).filter(([k]) => k.startsWith('$') || k.startsWith('_')))
            : {};
        return {
            ...listLevelMeta,
            groups,
            totalResults: response.totalResults,
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            hasNextPage: response.hasNextPage,
            hasPreviousPage: response.hasPreviousPage,
            itemsPerPage: response.itemsPerPage,
            startIndex: ((response.currentPage - 1) * response.itemsPerPage) + 1,
            Resources: groups
        };
    }

    /**
     * Search Groups with SCIM filter
     */
    async searchGroups(params: { filter?: string; page?: number; pageSize?: number; sortBy?: string; attributes?: string[]; abortSignal?: AbortSignal; searchtext?: string; searchAttributes?: string; omitSearchDialectExpression?: boolean }): Promise<GroupsResponse> {
        const { filter, page = 1, pageSize = 25, sortBy, attributes, abortSignal, searchtext, searchAttributes, omitSearchDialectExpression } = params;
        const response = await this.fetchEntities<any>('/entities/Group', page, pageSize, sortBy, filter, attributes, abortSignal, searchtext, searchAttributes, undefined, undefined, omitSearchDialectExpression);

        // Map to Pascal case properties to match the schema
        // Preserve ALL properties from the original response (including $Group, score properties, etc.)
        const groups = response.data.map((group: any) => {
            // Store original raw data BEFORE any modifications (preserves all API response properties)
            const originalRawData = { ...group };

            // Start with a copy of all properties from the original group (preserves metadata like _skipReferenceExpansion, _attributesRequested)
            const mappedGroup: any = { ...group };

            // Ensure base properties use Pascal case (but don't overwrite if already present)
            mappedGroup.Id = group.Id || group.id || mappedGroup.Id;
            mappedGroup.DisplayName = group.DisplayName || group.displayName || mappedGroup.DisplayName;
            mappedGroup.Description = group.Description || group.description || mappedGroup.Description;
            mappedGroup.Email = group.Email || group.email || mappedGroup.Email;
            mappedGroup.EntityType = group.EntityType || group.entityType || mappedGroup.EntityType;
            mappedGroup.AccountName = group.AccountName || group.accountName || mappedGroup.AccountName;
            mappedGroup.EntraId = group.EntraId || group.entraId || mappedGroup.EntraId;
            mappedGroup.Member = group.Member || group.member || mappedGroup.Member;
            mappedGroup.MemberOf = group.MemberOf || group.memberOf || mappedGroup.MemberOf;
            mappedGroup.MemberTransitive = group.MemberTransitive || group.memberTransitive || mappedGroup.MemberTransitive;
            mappedGroup.Scope = group.Scope || group.scope || mappedGroup.Scope;
            mappedGroup.Type = group.Type || group.type || mappedGroup.Type;

            // Dynamically include any additional attributes requested (if not already present)
            if (attributes && attributes.length > 0) {
                attributes.forEach(attrName => {
                    // Map attribute name to both PascalCase and camelCase
                    const pascalCase = attrName.charAt(0).toUpperCase() + attrName.slice(1);
                    const camelCase = attrName.charAt(0).toLowerCase() + attrName.slice(1);
                    const value = group[pascalCase] || group[camelCase] || group[attrName];
                    if (value !== undefined && mappedGroup[pascalCase] === undefined) {
                        mappedGroup[pascalCase] = value;
                    }
                });
            }

            // Explicitly store rawData for consistency with other entity types (like classes)
            // This ensures all properties from the API response are accessible, including $Group, score properties, etc.
            // Store the original raw data (before any property mapping) to preserve all API response properties
            mappedGroup.rawData = originalRawData;

            return mappedGroup;
        });

        const listLevelMeta = response && typeof response === 'object'
            ? Object.fromEntries(Object.entries(response).filter(([k]) => k.startsWith('$') || k.startsWith('_')))
            : {};
        return {
            ...listLevelMeta,
            groups,
            totalResults: response.totalResults,
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            hasNextPage: response.hasNextPage,
            hasPreviousPage: response.hasPreviousPage,
            itemsPerPage: response.itemsPerPage,
            startIndex: ((response.currentPage - 1) * response.itemsPerPage) + 1,
            Resources: groups
        };
    }

    /**
     * Get vacancies from the API
     */
    async getVacancies(page: number = 1, filter?: string, sortBy?: string): Promise<PaginatedResponse<Vacancy> & { filter?: string }> {
        const response = await this.fetchEntities<Vacancy>('/api/scim/v2/entities/Vacancy', page, 25, sortBy, filter);
        return {
            ...response,
            filter
        };
    }

    /**
     * Get entity by ID (private implementation)
     */
    private async getEntityByIdInternal<T>(endpoint: string, id: string, params?: any): Promise<T> {
        const requestParams = params || {};
        await this.addDebugParam(requestParams);
        const apiResponse = await this.api.get(`${endpoint}/${id}`, {
            params: requestParams
        });

        // Copy entity data before tagging to avoid mutating the original response
        const entity = apiResponse.data;
        if (entity && typeof entity === 'object') {
            // Create a shallow copy to avoid mutating the original
            const taggedEntity = { ...entity };

            // Tag skipReferenceExpansion
            if (params?.skipReferenceExpansion !== undefined) {
                (taggedEntity as any)._skipReferenceExpansion = params.skipReferenceExpansion;
            }
            // Tag _attributesRequested only if attributes were actually provided and not empty (for backwards compatibility)
            let attributesArray: string[] = [];
            if (params?.attributes) {
                attributesArray = Array.isArray(params.attributes)
                    ? (params.attributes.length > 0 ? params.attributes : [])
                    : typeof params.attributes === 'string' && params.attributes.trim()
                        ? params.attributes.split(',').map((a: string) => a.trim()).filter((a: string) => a.length > 0)
                        : [];
            }
            if (attributesArray.length > 0) {
                (taggedEntity as any)._attributesRequested = [...attributesArray];
            }

            return taggedEntity;
        }

        return entity;
    }

    /**
     * Get Responsibility Center by ID
     */
    async getResponsibilityCenterById(id: string, skipReferenceExpansion: boolean = false, attributes?: string[]): Promise<any> {
        const params: any = {};
        params.skipReferenceExpansion = skipReferenceExpansion;
        if (Array.isArray(attributes) && attributes.length > 0) {
            const sanitized = attributes.map((a) => (a ?? '').toString().trim().replace(/^\$\./, '')).filter((a) => a.length > 0);
            if (sanitized.length > 0) params.attributes = sanitized.join(',');
        }
        return this.getEntityByIdInternal<ResponsibilityCenter>('/entities/ResponsibilityCenter', id, params);
    }

    /**
     * Get School by ID
     */
    async getSchoolById(id: string, skipReferenceExpansion: boolean = false, attributes?: string[]): Promise<any> {
        const params: any = {};
        params.skipReferenceExpansion = skipReferenceExpansion;
        if (Array.isArray(attributes) && attributes.length > 0) {
            const sanitized = attributes.map((a) => (a ?? '').toString().trim().replace(/^\$\./, '')).filter((a) => a.length > 0);
            if (sanitized.length > 0) params.attributes = sanitized.join(',');
        }
        return this.getEntityByIdInternal<School>('/entities/School', id, params);
    }

    /**
     * Get Building by ID
     */
    async getBuildingById(id: string, skipReferenceExpansion: boolean = false, attributes?: string[]): Promise<any> {
        const params: any = {};
        params.skipReferenceExpansion = skipReferenceExpansion;
        if (Array.isArray(attributes) && attributes.length > 0) {
            const sanitized = attributes.map((a) => (a ?? '').toString().trim().replace(/^\$\./, '')).filter((a) => a.length > 0);
            if (sanitized.length > 0) params.attributes = sanitized.join(',');
        }
        return this.getEntityByIdInternal<Building>('/entities/Building', id, params);
    }

    /**
     * Get Class by ID
     */
    async getClassById(id: string, skipReferenceExpansion: boolean = false, attributes?: string[]): Promise<any> {
        const params: any = {};
        params.skipReferenceExpansion = skipReferenceExpansion;
        if (Array.isArray(attributes) && attributes.length > 0) {
            const sanitized = attributes.map((a) => (a ?? '').toString().trim().replace(/^\$\./, '')).filter((a) => a.length > 0);
            if (sanitized.length > 0) params.attributes = sanitized.join(',');
        }
        return this.getEntityByIdInternal<Class>('/entities/Class', id, params);
    }

    /**
     * Get Group by ID
     */
    async getGroupById(id: string, skipReferenceExpansion: boolean = false, attributes?: string[]): Promise<any> {
        // Construct params with attributes last to ensure proper parameter order in URL
        const params: any = {};
        // Always set skipReferenceExpansion explicitly to ensure backend gets the correct value
        // Default is false (expand references) for detail views
        params.skipReferenceExpansion = skipReferenceExpansion;
        if (Array.isArray(attributes) && attributes.length > 0) {
            const sanitized = attributes
                .map((a) => (a ?? '').toString().trim().replace(/^\$\./, ''))
                .filter((a) => a.length > 0);
            if (sanitized.length > 0) {
                params.attributes = sanitized.join(',');
            }
            if (attributes.length > 12) {
                debugVerboseLog('[ScimEntityService.getEntityById] Requesting long attribute list to skip unindexed properties', { count: attributes.length, entityType: 'Group', id });
            }
        }
        return this.getEntityByIdInternal<ScimGroup>('/entities/Group', id, params);
    }

    /**
     * Generic $pivot call for any entity type.
     * GET .../entities/{entityType}/{entityId}/$pivot/{pivotId}
     */
    async callEntityPivot(
        entityType: string,
        entityId: string,
        pivotId: string,
        skipReferenceExpansionOrOptions?: boolean | CallEntityPivotOptions,
        options?: CallEntityPivotOptions
    ): Promise<any> {
        const path = `/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/$pivot/${encodeURIComponent(pivotId)}`;
        const params: any = {};
        const resolvedOptions: CallEntityPivotOptions | undefined =
            (typeof skipReferenceExpansionOrOptions === 'object' && skipReferenceExpansionOrOptions != null)
                ? skipReferenceExpansionOrOptions
                : options;
        const skipReferenceExpansion =
            typeof skipReferenceExpansionOrOptions === 'boolean'
                ? skipReferenceExpansionOrOptions
                : resolvedOptions?.skipReferenceExpansion;

        if (skipReferenceExpansion !== undefined) params.skipReferenceExpansion = skipReferenceExpansion;

        // Server-side list params (SCIM-style)
        const page = resolvedOptions?.page ?? 1;
        const pageSize = resolvedOptions?.pageSize ?? 25;
        if (page != null && pageSize != null) {
            const startIndex = ((page - 1) * pageSize) + 1;
            params.startIndex = startIndex;
            params.count = pageSize;
        }
        if (resolvedOptions?.sortBy) params.sortBy = resolvedOptions.sortBy;
        if (resolvedOptions?.searchtext) {
            // Backend expects camelCase searchText (matches fetchEntities)
            params.searchText = resolvedOptions.searchtext;
            params.searchdialect = resolvedOptions.omitSearchDialectExpression ? 'freetext' : 'expression';
        }
        if (resolvedOptions?.searchAttributes) params.searchAttributes = resolvedOptions.searchAttributes;
        if (resolvedOptions?.filter) params.filter = resolvedOptions.filter;

        await this.addDebugParam(params);
        const response = await this.api.get(path, {
            params,
            signal: resolvedOptions?.abortSignal,
            ...(resolvedOptions?.onBehalfOfUpn ? { meta: { onBehalfOfUpn: resolvedOptions.onBehalfOfUpn } } : {})
        });
        return response.data;
    }

    /**
     * Get pivot list for a request approval type (next layer when expanding tier1 RequestApprovalTypeSummary).
     * GET .../entities/requestapprovaltypes/{approvalTypeId}/$pivot
     * Returns array of { $Summary?, Id, EntityType, DisplayName } (Possessive types); Generic may return 501/empty.
     */
    async callRequestApprovalTypePivotList(approvalTypeId: string): Promise<RequestApprovalTypePivotListItem[]> {
        const path = `/entities/requestapprovaltypes/${encodeURIComponent(approvalTypeId)}/$pivot`;
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.get(path, { params });
        return Array.isArray(response.data) ? response.data : [];
    }

    /**
     * Submit an approval decision for a RequestApproval entity.
     * POST api/scim/v2/entities/RequestApproval/{id}/$decision
     */
    async callRequestApprovalDecision(
        requestApprovalId: string,
        decision: 'Approved' | 'Denied',
        reason: string
    ): Promise<any> {
        const id = (requestApprovalId ?? '').toString().trim();
        if (!id) return Promise.reject(new Error('RequestApproval id is required'));
        const path = `/entities/RequestApproval/${encodeURIComponent(id)}/$decision`;
        const params: any = {};
        await this.addDebugParam(params);
        const body = { ApprovalDecision: decision, Reason: reason };
        const response = await this.api.post(path, body, { params });
        return response.data;
    }

    /**
     * Call the $pivot action for a PluginFunction entity.
     * GET .../entities/pluginfunctions/{entityId}/$pivot
     * Response body is typically a **JSON array** of row objects; some servers may return a SCIM list envelope with `Resources`.
     * Optional `options` mirror query params used on `$pivot/{entityId}` (`searchText`, `searchdialect`, `searchAttributes`, `filter`) when the root endpoint supports them.
     * Sorting and paging of the returned rows remain client-side unless the server returns SCIM `totalResults` / paging metadata.
     */
    async callPluginFunctionPivot(
        entityId: string,
        abortSignal?: AbortSignal,
        options?: {
            filter?: string;
            searchtext?: string;
            searchAttributes?: string;
            omitSearchDialectExpression?: boolean;
        }
    ): Promise<any> {
        const path = getPluginFunctionPivotApiPath(entityId);
        const params: any = {};
        const o = options ?? {};
        if (o.filter != null && o.filter !== '') params.filter = o.filter;
        if (o.searchtext != null && o.searchtext !== '') {
            params.searchText = o.searchtext;
            params.searchdialect = o.omitSearchDialectExpression ? 'freetext' : 'expression';
        }
        if (o.searchAttributes != null && o.searchAttributes !== '') params.searchAttributes = o.searchAttributes;
        await this.addDebugParam(params);
        const response = await this.api.get(path, { params, signal: abortSignal });
        return response.data;
    }

    /**
     * Call the $pivot action for a PluginFunction with a specific entity ID.
     * GET .../entities/pluginfunctions/{pluginId}/$pivot/{entityId}
     * Supports list params: startIndex, count, filter, searchtext, searchAttributes.
     */
    async callPluginFunctionPivotWithEntity(
        pluginId: string,
        entityId: string,
        page: number = 1,
        pageSize: number = 25,
        sortBy?: string,
        filter?: string,
        searchtext?: string,
        searchAttributes?: string,
        omitSearchDialectExpression?: boolean,
        abortSignal?: AbortSignal
    ): Promise<any> {
        const path = getPluginFunctionPivotEntityApiPath(pluginId, entityId);
        const startIndex = ((page - 1) * pageSize) + 1;
        const params: any = { startIndex, count: pageSize };
        if (sortBy != null && sortBy !== '') params.sortBy = sortBy;
        if (filter != null && filter !== '') params.filter = filter;
        if (searchtext != null && searchtext !== '') {
            params.searchText = searchtext;
            params.searchdialect = omitSearchDialectExpression ? 'freetext' : 'expression';
        }
        if (searchAttributes != null && searchAttributes !== '') params.searchAttributes = searchAttributes;
        await this.addDebugParam(params);
        const response = await this.api.get(path, { params, signal: abortSignal });
        return response.data;
    }

    /**
     * Call the $details action for a PluginFunction $pivot entity result.
     * GET .../entities/pluginfunctions/{pluginId}/$pivot/{entityId}/$details/{detailId}
     */
    async callPluginFunctionPivotDetails(pluginId: string, entityId: string, detailId: string, abortSignal?: AbortSignal): Promise<any> {
        const path = getPluginFunctionPivotDetailsApiPath(pluginId, entityId, detailId);
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.get(path, { params, signal: abortSignal });
        return response.data;
    }

    /**
     * Invoke a function at the detail view endpoint.
     * POST .../entities/pluginfunctions/{pluginId}/$pivot/{pivotEntityId}/$invoke/{targetId}
     * targetId is the id of the thing selected on the screen before (e.g. detail id from the URL).
     */
    async invokePluginFunction(pluginId: string, pivotEntityId: string, targetId: string, body: Record<string, string>): Promise<any> {
        const path = getPluginFunctionInvokeApiPath(pluginId, pivotEntityId, targetId);
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.post(path, body, { params });
        return response.data;
    }

    /**
     * Generic method to fetch entities by type
     */
    async getEntitiesByType(entityType: string, page: number = 1, pageSize: number = 25, sortBy?: string, filter?: string, attributes?: string[], abortSignal?: AbortSignal, searchtext?: string, searchAttributes?: string, onBehalfOfUpn?: string, countOnly?: boolean, omitSearchDialectExpression?: boolean, skipReferenceExpansion: boolean = true): Promise<PaginatedResponse<any>> {
        const endpoint = `/entities/${entityType}`;
        return this.fetchEntities<any>(endpoint, page, pageSize, sortBy, filter, attributes, abortSignal, searchtext, searchAttributes, onBehalfOfUpn, countOnly, omitSearchDialectExpression, skipReferenceExpansion);
    }

    /**
     * Fetch a paginated page of entities for a specific entity's reference property.
     * GET api/scim/v2/entities/{entityType}/{entityId}/{propertyAttributeDisplayName}?startIndex=...&count=...
     * Used for large array-ref attributes (e.g. ManualSetMember with Count > 50) in the paginated viewer.
     * When provided, attributes are sent as the attributes query param (same semantics as entity table requests).
     */
    async getEntityPropertyPage(
        entityType: string,
        entityId: string,
        propertyAttributeDisplayName: string,
        page: number = 1,
        pageSize: number = 25,
        abortSignal?: AbortSignal,
        attributes?: string[]
    ): Promise<PaginatedResponse<any>> {
        const path = `/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/${encodeURIComponent(propertyAttributeDisplayName)}`;
        const startIndex = ((page - 1) * pageSize) + 1;
        const params: any = { startIndex, count: pageSize, skipReferenceExpansion: true };
        if (attributes && attributes.length > 0) {
            params.attributes = attributes.join(',');
        }
        await this.addDebugParam(params);
        const apiResponse = await this.api.get(path, { params: params, signal: abortSignal });
        const responseData = apiResponse.data;
        const entities = responseData.Resources ?? responseData.items ?? responseData.data ?? [];
        const totalResults = responseData.totalResults ?? 0;
        const itemsPerPage = responseData.itemsPerPage ?? pageSize;
        const totalPages = Math.ceil(totalResults / itemsPerPage);
        return {
            data: entities,
            items: entities,
            totalResults,
            currentPage: page,
            totalPages: Math.max(1, totalPages),
            hasNextPage: page < totalPages,
            hasPreviousPage: page > 1,
            itemsPerPage,
        };
    }

    /**
     * Get content types from mgmt endpoint
     * Returns direct array of content types: [{Id, Ext, ContentType, Version}, ...]
     */
    async getContentTypes(abortSignal?: AbortSignal): Promise<any[]> {
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.get('/mgmt/contenttypes', {
            params: params,
            signal: abortSignal
        });

        const data = response.data;

        // Handle direct array response (expected format)
        if (Array.isArray(data)) {
            return data;
        }

        // Handle wrapper structures (fallback for edge cases)
        if (data && typeof data === 'object') {
            if (Array.isArray(data.data)) {
                return data.data;
            }
            if (Array.isArray(data.Resources)) {
                return data.Resources;
            }
            if (Array.isArray(data.results)) {
                return data.results;
            }
            if (Array.isArray(data.Result)) {
                return data.Result;
            }
        }

        // Default to empty array if we can't find an array
        console.warn('ContentTypes response is not an array, returning empty array. Response:', data);
        return [];
    }

    /**
     * Get function catalog from mgmt endpoint
     * Returns direct array of function catalog entries
     */
    async getFunctionCatalog(abortSignal?: AbortSignal, onBehalfOfUpn?: string, countOnly?: boolean): Promise<any[]> {
        const params: any = {};
        if (countOnly === true) {
            params.countOnly = true;
        }
        await this.addDebugParam(params);
        const response = await this.api.get('/mgmt/FunctionCatalog', {
            params: params,
            signal: abortSignal,
            ...(onBehalfOfUpn != null && onBehalfOfUpn !== '' ? { meta: { onBehalfOfUpn } } : {})
        });

        const data = response.data;

        // Handle direct array response (expected format)
        if (Array.isArray(data)) {
            return data;
        }

        // Handle wrapper structures (fallback for edge cases)
        if (data && typeof data === 'object') {
            if (Array.isArray(data.data)) {
                return data.data;
            }
            if (Array.isArray(data.Resources)) {
                return data.Resources;
            }
            if (Array.isArray(data.results)) {
                return data.results;
            }
            if (Array.isArray(data.Result)) {
                return data.Result;
            }
        }

        // Default to empty array if we can't find an array
        console.warn('FunctionCatalog response is not an array, returning empty array. Response:', data);
        return [];
    }

    /**
     * Get a single function catalog entry by ID (inputs and metadata for "Prepare invoke").
     * GET /mgmt/functioncatalog/{catalogId}
     */
    async getFunctionCatalogEntry(catalogId: string): Promise<any> {
        if (!catalogId?.trim()) {
            return Promise.reject(new Error('Catalog ID is required'));
        }
        const path = `/mgmt/functioncatalog/${encodeURIComponent(catalogId.trim())}`;
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.get(path, { params });
        return response.data;
    }

    /**
     * Invoke a function catalog entry by ID (POST prepared input values to same endpoint).
     * POST /mgmt/functioncatalog/{catalogId}
     */
    async invokeFunctionCatalog(catalogId: string, body: Record<string, string>): Promise<any> {
        if (!catalogId?.trim()) {
            return Promise.reject(new Error('Catalog ID is required'));
        }
        const path = `/mgmt/functioncatalog/${encodeURIComponent(catalogId.trim())}`;
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.post(path, body, { params });
        return response.data;
    }

    /**
     * Get text index information from mgmt endpoint
     * Returns direct array of text index data: [{Name, ItemCount, IndexSizeMB, MergeStatus, PopulateCompletionAge, ImportStatus, LastPopulated, PopulateStatus}, ...]
     */
    async getTextIndex(abortSignal?: AbortSignal): Promise<any[]> {
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.get('/mgmt/textindex', {
            params: params,
            signal: abortSignal
        });

        const data = response.data;

        // Handle direct array response (expected format)
        if (Array.isArray(data)) {
            return data;
        }

        // Handle wrapper structures (fallback for edge cases)
        if (data && typeof data === 'object') {
            if (Array.isArray(data.data)) {
                return data.data;
            }
            if (Array.isArray(data.Resources)) {
                return data.Resources;
            }
            if (Array.isArray(data.results)) {
                return data.results;
            }
            if (Array.isArray(data.Result)) {
                return data.Result;
            }
        }

        // Default to empty array if we can't find an array
        console.warn('TextIndex response is not an array, returning empty array. Response:', data);
        return [];
    }

    /**
     * Get build time information from mgmt endpoint
     * Returns the build time data (format may vary)
     */
    async getBuildTime(abortSignal?: AbortSignal): Promise<any> {
        const params: any = {};
        await this.addDebugParam(params);
        const response = await this.api.get('/mgmt/buildtime', {
            params: params,
            signal: abortSignal
        });

        const data = response.data;

        // Handle direct object response
        if (data && typeof data === 'object') {
            return data;
        }

        // Default to empty object if we can't parse
        console.warn('BuildTime response is not an object, returning empty object. Response:', data);
        return {};
    }

    /**
     * Fetch IsReferenceTo attributes for PossessiveActorsAttributes when PossessiveTargetAttribute
     * can reference multiple entity types. New API shape; call as extra for now until ready to switch.
     * GET entities/schemaentities/{possessiveScope}/attributes/{possessiveTargetAttribute}/IsReferenceTo/attributes?filter=IsReference eq true
     */
    async getPossessiveActorsAttributesIsReferenceTo(possessiveScope: string, possessiveTargetAttribute: string, abortSignal?: AbortSignal): Promise<PaginatedResponse<any> | null> {
        if (!possessiveScope?.trim() || !possessiveTargetAttribute?.trim()) return null;
        const scope = possessiveScope.trim();
        const attr = possessiveTargetAttribute.trim();
        const endpoint = `/entities/schemaentities/${encodeURIComponent(scope)}/attributes/${encodeURIComponent(attr)}/IsReferenceTo/attributes`;
        const params: Record<string, string> = { filter: 'IsReference eq true' };
        await this.addDebugParam(params);
        try {
            const response = await this.api.get(endpoint, { params, signal: abortSignal });
            const data = response?.data;
            if (data && typeof data === 'object') {
                const resources = data.Resources ?? data.resources ?? (Array.isArray(data) ? data : []);
                const totalResults = data.totalResults ?? (Array.isArray(resources) ? resources.length : 0);
                return {
                    data: Array.isArray(resources) ? resources : [],
                    items: Array.isArray(resources) ? resources : [],
                    totalResults: Number(totalResults) || 0,
                    currentPage: 1,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPreviousPage: false,
                    itemsPerPage: Number(totalResults) || 0
                };
            }
            return null;
        } catch {
            return null;
        }
    }

    /**
     * TODO: For future reference if needed
     * Method to fetch bindings for a specific schema attribute
     * URL format: /entities/schemaattribute/{attributeId}/bindings
     * Example: /entities/schemaattribute/$.type/bindings
     */
    async getAttributeBindings(attributeId: string, page: number = 1, pageSize: number = 25, sortBy?: string, filter?: string, attributes?: string[]): Promise<PaginatedResponse<any>> {
        const endpoint = `/entities/schemaattribute/${attributeId}/bindings`;

        const startIndex = ((page - 1) * pageSize) + 1;
        const params: any = {
            startIndex,
            count: pageSize,
            skipReferenceExpansion: true
        };

        if (sortBy) {
            params.sortBy = sortBy;
        }

        if (attributes && attributes.length > 0) {
            params.attributes = attributes.join(',');
        }

        if (filter) {
            params.filter = filter;
        }

        // Add Debug parameter if debug mode is enabled
        await this.addDebugParam(params);

        const apiResponse = await this.api.get(endpoint, {
            params: params
        });

        const responseData = apiResponse.data;
        const entities = responseData.Resources || [];
        const totalResults = responseData.totalResults || 0;
        const itemsPerPage = responseData.itemsPerPage || pageSize;
        const hasMore = responseData.$hasMore !== undefined ? responseData.$hasMore : undefined;
        const totalPages = Math.ceil(totalResults / itemsPerPage);
        const hasNextPage = hasMore !== undefined ? hasMore : (page < totalPages);
        const hasPreviousPage = page > 1;

        const listLevelDollarProps = responseData && typeof responseData === 'object'
            ? Object.fromEntries(Object.entries(responseData).filter(([key]) => key.startsWith('$')))
            : {};
        const listLevelUnderscoreProps = responseData && typeof responseData === 'object'
            ? Object.fromEntries(Object.entries(responseData).filter(([key]) => key.startsWith('_')))
            : {};

        // Tag entities; copy list-level _ onto each item
        const taggedEntities = entities.map((entity: any) => {
            if (entity && typeof entity === 'object') {
                const taggedEntity = { ...entity, ...listLevelUnderscoreProps };
                taggedEntity._skipReferenceExpansion = true;
                if (attributes && attributes.length > 0) {
                    taggedEntity._attributesRequested = [...attributes];
                }
                return taggedEntity;
            }
            return entity;
        });

        const firstTagged = taggedEntities[0];
        const clientUnderscoreProps = firstTagged && typeof firstTagged === 'object'
            ? Object.fromEntries(Object.entries(firstTagged).filter(([k]) => k.startsWith('_')))
            : {};

        return {
            ...listLevelDollarProps,
            ...listLevelUnderscoreProps,
            ...clientUnderscoreProps,
            data: taggedEntities,
            items: taggedEntities,
            totalResults,
            currentPage: page,
            totalPages,
            hasNextPage,
            hasPreviousPage,
            itemsPerPage,
            hasMore
        };
    }

    /**
     * Fetch schema entity bindings for an entity type.
     * Called when opening create/edit dialogs so the backend can prime or use bindings for that type.
     * URL format: /entities/schemaentity/{entityType}/bindings (e.g. /entities/schemaentity/group/bindings)
     */
    async getSchemaEntityBindings(entityType: string): Promise<any> {
        const normalized = (entityType || '').trim().toLowerCase();
        if (!normalized) {
            return Promise.resolve({ Resources: [], totalResults: 0 });
        }
        const endpoint = `/entities/schemaentity/${normalized}/bindings`;
        const params: any = { skipReferenceExpansion: true };
        await this.addDebugParam(params);
        const apiResponse = await this.api.get(endpoint, { params });
        return apiResponse.data;
    }

    /**
     * Fetch a single schema entity by entity type (e.g. Person).
     * Used to check if an entity type has an Entities property (allowed for ManualSetMember).
     * URL format: /entities/schemaentity/{entityTypeId} (e.g. api/scim/v2/entities/schemaentity/Person)
     */
    async getSchemaEntity(entityTypeId: string, abortSignal?: AbortSignal): Promise<Record<string, unknown> | null> {
        const id = (entityTypeId || '').trim();
        if (!id) return null;
        const endpoint = `/entities/schemaentity/${encodeURIComponent(id)}`;
        try {
            const params: any = { skipReferenceExpansion: true };
            await this.addDebugParam(params);
            const response = await this.api.get(endpoint, { params, signal: abortSignal });
            const data = response?.data;
            return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
        } catch {
            return null;
        }
    }

    /**
     * Method to fetch entities with a specific sub-entity type (for SchemaEntity)
     * This handles the special case where sub-entities return a single object with an Attributes array
     */
    async getEntitiesByTypeWithSubType(entityType: string, subType: string, page: number = 1, pageSize: number = 25, sortBy?: string, filter?: string, attributes?: string[]): Promise<PaginatedResponse<any>> {
        const endpoint = `/entities/${entityType}/${subType}`;

        const startIndex = ((page - 1) * pageSize) + 1;
        const params: any = {
            startIndex,
            count: pageSize,
            skipReferenceExpansion: true
        };

        if (sortBy) {
            params.sortBy = sortBy;
        }

        if (attributes && attributes.length > 0) {
            params.attributes = attributes.join(',');
        }

        if (filter) {
            // Filter comes last to ensure proper parameter order in URL
            params.filter = filter;
        }

        // Add Debug parameter if debug mode is enabled
        await this.addDebugParam(params);

        const apiResponse = await this.api.get(endpoint, {
            params: params
        });

        const responseData = apiResponse.data;

        // For sub-entities, the response is a single object with an Attributes array
        // not a paginated response with Resources array
        if (responseData.Attributes && Array.isArray(responseData.Attributes)) {

            // Tag attributes with metadata
            // Create copies to avoid mutating the original response
            const taggedAttributes = responseData.Attributes.map((attr: any) => {
                if (attr && typeof attr === 'object') {
                    // Create a shallow copy to avoid mutating the original
                    const taggedAttr = { ...attr };
                    taggedAttr._skipReferenceExpansion = true;
                    if (attributes && attributes.length > 0) {
                        taggedAttr._attributesRequested = [...attributes];
                    }
                    return taggedAttr;
                }
                return attr;
            });

            return {
                data: taggedAttributes,
                items: taggedAttributes,
                totalResults: responseData.Attributes.length,
                currentPage: 1,
                totalPages: 1,
                hasNextPage: false,
                hasPreviousPage: false,
                itemsPerPage: responseData.Attributes.length
            };
        }

        // Fallback to regular processing if it's not a sub-entity response
        const entities = responseData.Resources || [];
        const totalResults = responseData.totalResults || 0;
        const itemsPerPage = responseData.itemsPerPage || pageSize;
        const hasMore = responseData.$hasMore !== undefined ? responseData.$hasMore : undefined;
        const totalPages = Math.ceil(totalResults / itemsPerPage);
        const hasNextPage = hasMore !== undefined ? hasMore : (page < totalPages);
        const hasPreviousPage = page > 1;

        const listLevelDollarProps = responseData && typeof responseData === 'object'
            ? Object.fromEntries(Object.entries(responseData).filter(([key]) => key.startsWith('$')))
            : {};
        const listLevelUnderscoreProps = responseData && typeof responseData === 'object'
            ? Object.fromEntries(Object.entries(responseData).filter(([key]) => key.startsWith('_')))
            : {};

        // Tag entities; copy list-level _ onto each item
        const taggedEntities = entities.map((entity: any) => {
            if (entity && typeof entity === 'object') {
                const taggedEntity = { ...entity, ...listLevelUnderscoreProps };
                // Tag skipReferenceExpansion (always true for getEntitiesByTypeWithSubType)
                taggedEntity._skipReferenceExpansion = true;
                if (attributes && attributes.length > 0) {
                    taggedEntity._attributesRequested = [...attributes];
                }
                return taggedEntity;
            }
            return entity;
        });

        const firstTagged = taggedEntities[0];
        const clientUnderscoreProps = firstTagged && typeof firstTagged === 'object'
            ? Object.fromEntries(Object.entries(firstTagged).filter(([k]) => k.startsWith('_')))
            : {};

        return {
            ...listLevelDollarProps,
            ...listLevelUnderscoreProps,
            ...clientUnderscoreProps,
            data: taggedEntities,
            items: taggedEntities,
            totalResults,
            currentPage: page,
            totalPages,
            hasNextPage,
            hasPreviousPage,
            itemsPerPage,
            hasMore
        };
    }

    /**
     * Generic method to get entity by ID and type
     */
    async getEntityById(id: string, entityType: string, skipReferenceExpansion: boolean = false, attributes?: string[]): Promise<any> {
        const lowerEntityType = entityType.toLowerCase();

        // Handle special cases that need specific methods
        switch (lowerEntityType) {
            case 'responsibilitycenter':
                return this.getResponsibilityCenterById(id, skipReferenceExpansion, attributes);
            case 'school':
                return this.getSchoolById(id, skipReferenceExpansion, attributes);
            case 'building':
                return this.getBuildingById(id, skipReferenceExpansion, attributes);
            case 'class':
                return this.getClassById(id, skipReferenceExpansion, attributes);
            case 'group':
                return this.getGroupById(id, skipReferenceExpansion, attributes);
            case 'person':
            case 'user':
                // For Person and User entities, fall back to ScimUserService
                const { ScimUserService } = await import('./ScimUserService');
                const userService = new ScimUserService();
                return userService.getUser(id);
        }

        // For all other entity types, use the generic endpoint
        // This makes the system truly dynamic - no need to add new cases for new entity types
        try {
            // Construct params with attributes last to ensure proper parameter order in URL
            const params: any = {};
            // Always set skipReferenceExpansion explicitly to ensure backend gets the correct value
            // Backend defaults to true, so we must explicitly pass false for detail views
            params.skipReferenceExpansion = skipReferenceExpansion;
            // Only add attributes to URL when non-empty; send as comma-separated string to avoid attributes[]=... per item.
            // API expects plain names (e.g. "Config"), not "$.Config" — strip leading "$." from each.
            if (Array.isArray(attributes) && attributes.length > 0) {
                const sanitized = attributes
                    .map((a) => (a ?? '').toString().trim().replace(/^\$\./, ''))
                    .filter((a) => a.length > 0);
                if (sanitized.length > 0) {
                    params.attributes = sanitized.join(',');
                }
                if (attributes.length > 12) {
                    debugVerboseLog('[ScimEntityService.getEntityById] Requesting long attribute list to skip unindexed properties', { count: attributes.length, entityType, id });
                }
            }

            return await this.getEntityByIdInternal<any>(`/entities/${entityType}`, id, params);
        } catch (error) {
            console.error(`❌ ScimEntityService: Failed to fetch ${entityType} with ID ${id}:`, error);
            throw new Error(`Failed to fetch ${entityType} entity: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Generic method to patch an entity by ID and type
     */
    async patchEntity(id: string, patchData: any, entityType: string, abortSignal?: AbortSignal): Promise<any> {
        const endpoint = `/entities/${entityType}/${id}`;

        try {
            const params: any = {};
            await this.addDebugParam(params);
            const response = await this.api.patch(endpoint, patchData, {
                params: params,
                signal: abortSignal
            });

            // API returns { Entity: {...} } - extract the entity
            return response.data?.Entity || response.data;
        } catch (error) {
            console.error(`❌ ScimEntityService: Error patching ${entityType} entity:`, error);
            throw error;
        }
    }

    /**
     * Generic method to create a new entity
     */
    async createEntity(entityType: string, entityData: any, abortSignal?: AbortSignal): Promise<any> {
        const endpoint = `/entities/${entityType}`;

        try {
            const params: any = {};
            await this.addDebugParam(params);
            const response = await this.api.post(endpoint, entityData, {
                params: params,
                signal: abortSignal
            });

            // API returns { Entity: {...} } - extract the entity
            return response.data?.Entity || response.data;
        } catch (error) {
            console.error(`❌ ScimEntityService: Error creating ${entityType} entity:`, error);
            throw error;
        }
    }

    /**
     * Generic method to delete an entity by ID
     */
    async deleteEntity(entityType: string, entityId: string): Promise<void> {
        const endpoint = `/entities/${entityType}/${entityId}`;

        try {
            const params: any = {};
            await this.addDebugParam(params);
            await this.api.delete(endpoint, { params: params });
        } catch (error) {
            console.error(`❌ ScimEntityService: Error deleting ${entityType} entity:`, error);
            throw error;
        }
    }
}
